import { getIndicatorHash } from '../../../../common/utils/getIndicatorHash'
import { ResolvedOperand } from '../types'

const PRICE_FIELDS = new Set(['open', 'high', 'low', 'close'])
const ANGLE_SUFFIX = 'Angle'

/**
 * Operand side as stored on a condition's LHS/RHS. Kept structural (rather than
 * importing the loose context type) so the compile layer stays strict.
 */
export interface OperandSide {
  field?: string
  value?: unknown
  indicator?: { name?: string; params?: Record<string, unknown> }
  candle?: {
    offset?: number
    lookback?: { count: number; mode: 'all' | 'some'; needed?: number }
  }
}

/**
 * Mirrors PDS's resolution precedence (patternUtils.ts:791-805):
 * explicit value → constant; raw candle field → price series; otherwise the
 * indicator line keyed by the platform hash. Angle fields become slope
 * references against the parent line.
 */
export const resolveOperand = (side: OperandSide | undefined): ResolvedOperand | undefined => {
  if (!side || !side.indicator?.name) return undefined
  const offset = side.candle?.offset ?? 0
  const lookback = side.candle?.lookback

  if (side.value !== undefined && side.value !== null) {
    return { kind: 'constant', value: Number(side.value), offset, lookback }
  }

  const field = side.field ?? ''
  const indicator = { name: side.indicator.name, params: side.indicator.params }

  if (PRICE_FIELDS.has(field)) {
    return {
      kind: 'price',
      seriesId: `candle.${field}`,
      indicatorName: indicator.name,
      field,
      offset,
      lookback,
    }
  }

  if (field.endsWith(ANGLE_SUFFIX)) {
    const parentField = field.slice(0, -ANGLE_SUFFIX.length)
    return {
      kind: 'angle',
      seriesId: `${getIndicatorHash(indicator)}.${parentField}`,
      indicatorName: indicator.name,
      field: parentField,
      offset,
      lookback,
    }
  }

  return {
    kind: 'indicator',
    seriesId: `${getIndicatorHash(indicator)}.${field}`,
    indicatorName: indicator.name,
    field,
    offset,
    lookback,
  }
}
